<script setup lang="ts">
import ProgressSpinner from 'primevue/progressspinner';
import KpEchCodeSelect from '@/components/KpEchCodeSelect.vue';
import Card from 'primevue/card';
import { TYPES } from '@/models/EchCode';
import Toolbar from 'primevue/toolbar';
import { createModel } from '@kp/js-model';
import { computed, onMounted, ref, type Ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { errToToast, reallyCancel } from '@/helpers/tools';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import KpButton from '@/components/KpButton.vue';
import KpTextAreaField from '@/components/KpTextAreaField.vue';
import KpSelectField from '@/components/KpSelectField.vue';
import { GebaeudeklasseNutzungsart } from '@/models/GebaeudeklasseNutzungsart';
import { createValidator, createValidators, validateNotEmpty } from '@/helpers/model_validators';
import { Nutzungsart, NutzungsartCollection } from '@/models/Nutzungsart';

const props = defineProps<{
    // id is injected via router:
    id: number | 'new';
}>();

const router = useRouter();
const toast = useToast();
const record: Ref<GebaeudeklasseNutzungsart | null> = ref(null);
const saving = ref(false);

const { allValid, validators } = createValidators({
    gebaeudeklasse: createValidator(
        computed(() => record.value?.gebaeudeklasse_id),
        [validateNotEmpty],
    ),
    nutzungsart: createValidator(
        computed(() => record.value?.nutzungsart_id),
        [validateNotEmpty],
    ),
});

onMounted(() => {
    loadRecord(props.id);
});

onBeforeRouteLeave(() => {
    return reallyCancel(record?.value);
});

onBeforeRouteUpdate((to, from) => {
    if (to.params.id !== from.params.id) {
        loadRecord(to.params.id === 'new' ? 'new' : Number(to.params.id));
    }
});

async function loadRecord(id: number | 'new') {
    if (id === 'new') {
        record.value = createModel(GebaeudeklasseNutzungsart);
    } else {
        try {
            const rec = await GebaeudeklasseNutzungsart.loadById(id);
            if (rec) {
                record.value = rec;
            } else {
                record.value = null;
                throw new Error(getI18n().t('record_not_found'));
            }
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

async function onSave() {
    if (record?.value === null) {
        return;
    }
    saving.value = true;
    try {
        const isNew = !Number(record.value.id);
        await record.value.save();
        record.value.commit();
        toast.add({
            severity: 'success',
            detail: getI18n().t('datensatz_gespeichert'),
            life: 1000,
        });
        if (isNew) {
            router.push({ name: 'bg_geb_gebaeudeklasse_nutzungsart_edit', params: { id: record.value.id } });
        }
    } catch (e) {
        errToToast(e, toast);
    } finally {
        saving.value = false;
    }
}

function onCancel() {
    if (reallyCancel(record?.value)) {
        record?.value?.rollback();
        router.push({ name: 'bg_geb_gebaeudeklasse_nutzungsart' });
    }
}

const nutzungsarten: Ref<Nutzungsart[]> = ref([]);
onMounted(async () => {
    const col = new NutzungsartCollection();
    col.setQueryParams({
        sort: [
            {
                property: 'reihenfolge',
                direction: 'asc',
            },
        ],
    });
    await col.query();
    col.each((m) => nutzungsarten.value.push(m));
});
</script>
<template>
    <div v-if="!record" class="flex justify-content-center">
        <ProgressSpinner></ProgressSpinner>
    </div>
    <div v-else>
        <Card class="kp-max-content-width">
            <template #header>
                <Toolbar class="toolbar">
                    <template #start>
                        <h2>
                            <i class="fas fa-user"></i> {{ $t('zuordnung_nutzungsart') }}:
                            {{ record.id || $t('new') }}
                        </h2>
                    </template>
                    <template #end>
                        <div class="flex gap-1">
                            <KpButton
                                severity="success"
                                icon="far fa-floppy-disk"
                                v-tooltip="$t('speichern')"
                                :loading="saving"
                                :disabled="!allValid"
                                @click="onSave"
                            />
                            <KpButton
                                severity="secondary"
                                icon="far fa-xmark"
                                v-tooltip="$t('abbrechen')"
                                :disabled="saving"
                                @click="onCancel"
                            />
                        </div>
                    </template>
                </Toolbar>
            </template>
            <template #content>
                <div class="formgrid grid align-items-start">
                    <KpEchCodeSelect
                        v-model="record.gebaeudeklasse_id"
                        class="col-6"
                        fluid
                        :type="TYPES.gebaeudeklasse"
                        :label="$t('gebaeudeklasse')"
                        :invalid="!validators.gebaeudeklasse.isValid.value"
                        :error="validators.gebaeudeklasse.msg.value"
                    ></KpEchCodeSelect>
                    <KpSelectField
                        v-model="record.nutzungsart_id"
                        class="col-6"
                        fluid
                        :label="$t('nutzungsart')"
                        :options="nutzungsarten"
                        option-label="bezeichnung"
                        option-value="id"
                        :invalid="!validators.nutzungsart.isValid.value"
                        :error="validators.nutzungsart.msg.value"
                    ></KpSelectField>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpTextAreaField
                        v-model="record.bemerkung"
                        :label="$t('bemerkung')"
                        class="col-12"
                        fluid
                    ></KpTextAreaField>
                </div>
            </template>
        </Card>
    </div>
</template>

<style lang="css" scoped>
.toolbar {
    border: none;
    border-bottom: 1px solid var(--p-toolbar-border-color);
    h2 {
        margin: 0;
    }
}
</style>
